import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { ASSETS, COMPANY } from "../../data";
import { MaskLine } from "./Reveal";
import { scrollTo } from "../../hooks/useLenis";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-[var(--ink)] text-white"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={ASSETS.heroCap}
          alt="Blue HDPE bottle cap"
          className="h-full w-full object-cover object-[72%_center] opacity-25 sm:object-center sm:opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)]/85 via-[var(--ink)]/55 to-[var(--ink)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-[var(--ink)]/40 to-transparent" />
      </motion.div>
      <div className="grain" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 min-h-screen flex flex-col justify-center pt-24 pb-16"
      >
        <MaskLine delay={0.35}>
          <span className="font-mono text-[10px] sm:text-sm uppercase tracking-[0.22em] sm:tracking-[0.3em] text-[#5b8cff]">
            Nagpur · Food-Grade Caps & Closures
          </span>
        </MaskLine>

        <h1 className="font-display font-semibold tracking-tighter leading-[1.02] sm:leading-[0.92] mt-5 sm:mt-6 text-[2.4rem] sm:text-7xl lg:text-[6.5rem]">
          <MaskLine delay={0.5}>The cap is the</MaskLine>
          <MaskLine delay={0.62}>
            <span className="text-[#5b8cff]">first promise</span>
          </MaskLine>
          <MaskLine delay={0.74}>your brand makes.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1 }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-slate-300"
          data-testid="hero-subtext"
        >
          {COMPANY.legal} engineers high-quality plastic caps and closures for the food &
          beverage industry — safe, consistent and on-brand, molded in a 25,000 sq. ft. facility.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-product-btn"
            onClick={() => scrollTo("#product")}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[var(--ink)] hover:bg-[#5b8cff] hover:text-white active:scale-95 transition-[background-color,color,transform]"
          >
            Explore the ALASKA cap
            <ArrowDownRight size={17} className="group-hover:rotate-45 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-contact-btn"
            onClick={() => scrollTo("#contact")}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 active:scale-95 transition-[background-color,transform]"
          >
            Get a quote
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 right-5 md:right-10 z-10 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400"
      >
        Scroll to discover
      </motion.div>
    </section>
  );
};
